import ApplicationController from "@mixitone/mvc";
import type { To } from "@mixitone/router";
import { resetDocumentScroll, resetWindowScroll } from "lib/scroll";
import type { Location } from "react-router-dom";
import type { Path, useModals, useNavigate, useParams } from "../router";
import { EventBus } from "@mixitone/util";
import routeService from "services/RouteService";
import agentToolsService from "services/AgentToolsService";

interface State {
  navigate: ReturnType<typeof useNavigate>;
  modals: ReturnType<typeof useModals>;
  location: Location;
  urlAfterLogin?: Path;
  params: ReturnType<typeof useParams>;
  ready?: boolean;
}
interface Props {
  navigate: ReturnType<typeof useNavigate>;
  modals: ReturnType<typeof useModals>;
  location: Location;
  params: ReturnType<typeof useParams>;
}

type NavigateType = ReturnType<typeof useNavigate>;
type ModalsType = ReturnType<typeof useModals>;

export default class RouteController extends ApplicationController<State> {
  events: EventBus = new EventBus();

  async initialize(_props: never) {
    window.routeController = this;
    routeService.setController(this);

    this.addDependency(
      agentToolsService.addResources(this, {
        context: () => {
          return `The user is viewing page ${this.state.location.pathname} with params ${JSON.stringify(this.state.params)}`;
        },
      }),
    );
  }

  actionSetup(props: Props) {
    const previousPathname = this.state.location?.pathname;
    const previousModalsCount = this.state.location?.state?.modals?.length ?? 0;
    const pathname = props.location.pathname;
    const modalsCount = props.location.state?.modals?.length ?? 0;

    this.state.location = props.location;
    this.state.navigate = props.navigate;
    this.state.params = props.params;
    this.state.modals = props.modals;

    const pathnameChanged =
      this.state.ready && previousPathname !== undefined && previousPathname !== pathname;
    const modalsClosed = this.state.ready && modalsCount < previousModalsCount;

    this.state.ready = true;

    if (pathnameChanged) {
      resetDocumentScroll();
    } else if (modalsClosed) {
      resetWindowScroll();
    }
  }

  get params(): Record<string, string | undefined> {
    return this.state.params;
  }

  get modalParams(): Record<string, string | undefined> {
    return this.state.location.state || {};
  }

  getLocationState<T>(key: string): T | undefined {
    const state = this.state.location.state;
    return state && typeof state === "object" && key in state ? (state as any)[key] : undefined;
  }

  async actionBack() {
    this.state.navigate(-1);
  }

  /**
   * @example
   * routeService.openModal("modalName", { state: { foo: "bar" } });
   */
  public actionOpenModal: ModalsType["open"] = (...args) => {
    const [modal, options] = args;
    this.state.modals.open(modal, { ...options });

    this.events.emit("route:modalOpened", { modal, options });
  };

  public actionCloseModal: ModalsType["close"] = (...args) => {
    const options = args[0];
    this.state.modals.close({ ...options });
    resetWindowScroll();

    this.events.emit("route:modalClosed", { options });
  };

  public isModalOpen(): boolean {
    return this.state.modals.current.length > 0;
  }

  actionForward<P extends Path | To<Path> | number>(...args: Parameters<NavigateType>) {
    // @ts-expect-error
    this.state.navigate<P>(...args);
  }

  setUrlAfterLogin(url: Path | undefined) {
    this.state.urlAfterLogin = url;
  }
}
